import React, { useState, useRef, useEffect, forwardRef } from 'react';
import './CustomTimeSelect.css';
const DEFAULT_MASK_VALUE = '__:__ AM';
const DEFAULT_CURSOR_POSITION = 0;
const TIME_FORMAT_SEPARATOR = ':';
const TIME_DISPLAY_SEPARATOR = ':';
const AM_TEXT = 'AM';
const PM_TEXT = 'PM';
const TIME_SECONDS = '00';
const UNDERSCORE_PLACEHOLDER = '_';
const FOCUS_DELAY = 0;
const MIN_CURSOR_POS = 0;
const MAX_CURSOR_POS = 7;
const COLON_POS = 2;
const HOUR_FIRST_POS = 0;
const HOUR_SECOND_POS = 1;
const MINUTE_FIRST_POS = 3;
const MINUTE_SECOND_POS = 4;
const AMPM_FIRST_POS = 6;
const AMPM_SECOND_POS = 7;
const HOURS_IN_HALF_DAY = 12;
const PADDING_CHAR = '0';
const MIN_PADDING_LENGTH = 2;

const CustomTimeSelect = forwardRef(({ value, onChange, placeholder, children, label }, ref) => {
    const [isEditing, setIsEditing] = useState(false);
    const [customTimeMode, setCustomTimeMode] = useState(false);
    const [maskedValue, setMaskedValue] = useState(DEFAULT_MASK_VALUE);
    const [cursorPosition, setCursorPosition] = useState(DEFAULT_CURSOR_POSITION);
    const [showOptions, setShowOptions] = useState(false);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const optionsRef = useRef(null);
    useEffect(() => {
        if (value) {
            const [hours, minutes] = value.split(TIME_FORMAT_SEPARATOR);
            const hour = parseInt(hours, 10);
            const ampm = hour >= HOURS_IN_HALF_DAY ? PM_TEXT : AM_TEXT;
            updateMaskedValueFromTime(hours, minutes, ampm);
        } else {
            setMaskedValue(DEFAULT_MASK_VALUE);
        }
    }, [value, customTimeMode]);

    // Handle clicks outside of the component to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                if (isEditing) {
                    submitMaskedValue();
                }
                setShowOptions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isEditing]);

    const updateMaskedValueFromTime = (hours, minutes, ampm) => {
        const hour = parseInt(hours, 10);
        const displayHour = hour % HOURS_IN_HALF_DAY || HOURS_IN_HALF_DAY;
        const hourStr = displayHour.toString().padStart(MIN_PADDING_LENGTH, PADDING_CHAR);
        const minuteStr = minutes.toString().padStart(MIN_PADDING_LENGTH, PADDING_CHAR);
        const isPM = ampm === PM_TEXT;

        // Create masked value
        const maskedVal = `${hourStr}${TIME_DISPLAY_SEPARATOR}${minuteStr} ${isPM ? PM_TEXT : AM_TEXT}`;
        setMaskedValue(maskedVal);
    };

    const handleKeyPress = (e) => {
        e.preventDefault();
        const key = e.key;

        // Process digit entries
        if (/^\d$/.test(key)) {
            let newMaskedValue = maskedValue.split('');

            // Determine position to insert digit
            if (cursorPosition === HOUR_FIRST_POS) {
                // First hour digit
                newMaskedValue[cursorPosition] = key;
                setCursorPosition(HOUR_SECOND_POS); // Move to second hour digit
            } else if (cursorPosition === HOUR_SECOND_POS) {
                // Second hour digit
                newMaskedValue[cursorPosition] = key;
                setCursorPosition(MINUTE_FIRST_POS); // Skip colon, jump to first minute digit
            } else if (cursorPosition === MINUTE_FIRST_POS) {
                // First minute digit
                newMaskedValue[cursorPosition] = key;
                setCursorPosition(MINUTE_SECOND_POS); // Move to second minute digit
            } else if (cursorPosition === MINUTE_SECOND_POS) {
                // Second minute digit
                newMaskedValue[cursorPosition] = key;
                setCursorPosition(AMPM_FIRST_POS); // Jump to AM/PM position
            }

            setMaskedValue(newMaskedValue.join(''));
            setShowOptions(true); // Show options when typing
        }
        // Process AM/PM toggle
        else if (key.toLowerCase() === 'p') {
            let newMaskedValue = maskedValue.split('');
            newMaskedValue[AMPM_FIRST_POS] = 'P';
            newMaskedValue[AMPM_SECOND_POS] = 'M';
            setMaskedValue(newMaskedValue.join(''));
            setCursorPosition(AMPM_SECOND_POS);
        }
        else if (key.toLowerCase() === 'a') {
            let newMaskedValue = maskedValue.split('');
            newMaskedValue[AMPM_FIRST_POS] = 'A';
            newMaskedValue[AMPM_SECOND_POS] = 'M';
            setMaskedValue(newMaskedValue.join(''));
            setCursorPosition(AMPM_SECOND_POS);
        }
        // Process backspace
        else if (key === 'Backspace') {
            if (cursorPosition > MIN_CURSOR_POS) {
                let newMaskedValue = maskedValue.split('');
                if (cursorPosition === HOUR_SECOND_POS) {
                    newMaskedValue[HOUR_FIRST_POS] = UNDERSCORE_PLACEHOLDER;
                    setCursorPosition(HOUR_FIRST_POS);
                } else if (cursorPosition === COLON_POS || cursorPosition === MINUTE_FIRST_POS) {
                    newMaskedValue[HOUR_SECOND_POS] = UNDERSCORE_PLACEHOLDER;
                    setCursorPosition(HOUR_SECOND_POS);
                } else if (cursorPosition === MINUTE_SECOND_POS) {
                    newMaskedValue[MINUTE_FIRST_POS] = UNDERSCORE_PLACEHOLDER;
                    setCursorPosition(MINUTE_FIRST_POS);
                } else if (cursorPosition === 5) {
                    newMaskedValue[MINUTE_SECOND_POS] = UNDERSCORE_PLACEHOLDER;
                    setCursorPosition(MINUTE_SECOND_POS);
                } else if (cursorPosition === AMPM_SECOND_POS) {
                    newMaskedValue[AMPM_FIRST_POS] = 'A';
                    newMaskedValue[AMPM_SECOND_POS] = 'M';
                    setCursorPosition(AMPM_FIRST_POS);
                }

                setMaskedValue(newMaskedValue.join(''));
            }
        }
        // Handle navigation keys
        else if (key === 'ArrowLeft') {
            if (cursorPosition > MIN_CURSOR_POS) {
                let newPos = cursorPosition - 1;
                // Skip the colon when navigating
                if (newPos === COLON_POS) newPos = HOUR_SECOND_POS;
                setCursorPosition(newPos);
            }
        } else if (key === 'ArrowRight') {
            if (cursorPosition < MAX_CURSOR_POS) {
                let newPos = cursorPosition + 1;
                // Skip the colon when navigating
                if (newPos === COLON_POS) newPos = MINUTE_FIRST_POS;
                setCursorPosition(newPos);
            }
        } else if (key === 'Enter') {
            e.preventDefault();
            submitMaskedValue();
            setShowOptions(false);
        } else if (key === 'Tab') {
            submitMaskedValue();
            setShowOptions(false);
        } else if (key === 'Escape') {
            setShowOptions(false);
            submitMaskedValue();
        } else if (key === 'ArrowDown') {
            e.preventDefault();
            setShowOptions(true);
            if (optionsRef.current && optionsRef.current.firstChild) {
                optionsRef.current.firstChild.focus();
            }
        }
    };

    // Submit the masked value
    const submitMaskedValue = () => {
        if (maskedValue[HOUR_FIRST_POS] === UNDERSCORE_PLACEHOLDER || maskedValue[HOUR_SECOND_POS] === UNDERSCORE_PLACEHOLDER ||
            maskedValue[MINUTE_FIRST_POS] === UNDERSCORE_PLACEHOLDER || maskedValue[MINUTE_SECOND_POS] === UNDERSCORE_PLACEHOLDER) {
            // Not all digits entered, reset to original state
            setMaskedValue(DEFAULT_MASK_VALUE);
            setIsEditing(false);
            return;
        }
        // Parse the time from the masked input
        const parts = maskedValue.split(TIME_DISPLAY_SEPARATOR);
        const hourPart = parts[0].replace(new RegExp(UNDERSCORE_PLACEHOLDER, 'g'), PADDING_CHAR); // Replace underscores with zeros
        let hours = parseInt(hourPart, 10);

        // Handle minutes
        const minuteAmPmPart = parts[1].split(' ');
        const minutePart = minuteAmPmPart[0].replace(new RegExp(UNDERSCORE_PLACEHOLDER, 'g'), PADDING_CHAR);
        const minutes = parseInt(minutePart, 10);

        // Handle AM/PM
        const isPM = minuteAmPmPart[1].toUpperCase() === PM_TEXT;

        // Convert to 24-hour
        if (isPM) {
            if (hours < HOURS_IN_HALF_DAY) {
                hours += HOURS_IN_HALF_DAY;
            }
        } else {
            if (hours === HOURS_IN_HALF_DAY) {
                hours = 0;
            }
            if (hours >= HOURS_IN_HALF_DAY + 1) {
                hours = hours % HOURS_IN_HALF_DAY;
            }
        }
        const timeValue = `${hours.toString().padStart(MIN_PADDING_LENGTH, PADDING_CHAR)}${TIME_FORMAT_SEPARATOR}${minutes.toString().padStart(MIN_PADDING_LENGTH, PADDING_CHAR)}${TIME_FORMAT_SEPARATOR}${TIME_SECONDS}`;
        setCustomTimeMode(true);
        setIsEditing(false);

        const customEvent = {
            target: {
                value: timeValue,
                isManualEntry: true
            }
        };

        onChange(customEvent);
    };

    const handleInputClick = (e) => {
        const rect = e.target.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const totalWidth = rect.width;
        const charWidth = totalWidth / 8;

        let newPos = Math.floor(clickX / charWidth);

        // Adjust position based on rules
        if (newPos === COLON_POS) {
            newPos = clickX < rect.left + (charWidth * 2.5) ? HOUR_SECOND_POS : MINUTE_FIRST_POS;
        } else if (newPos === 5) {
            newPos = clickX < rect.left + (charWidth * 5.5) ? MINUTE_SECOND_POS : AMPM_FIRST_POS;
        } else if (newPos > MAX_CURSOR_POS) {
            newPos = MAX_CURSOR_POS;
        }
        newPos = Math.max(MIN_CURSOR_POS, Math.min(MAX_CURSOR_POS, newPos));

        setCursorPosition(newPos);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };
    const handleFieldClick = () => {
        setIsEditing(true);
        setShowOptions(true);
        setCursorPosition(DEFAULT_CURSOR_POSITION);
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, FOCUS_DELAY);
    };
    const handleOptionSelect = (optionValue) => {
        setCustomTimeMode(false);
        setIsEditing(false);
        setShowOptions(false);

        onChange({
            target: {
                value: optionValue
            }
        });
    };
    const getOptions = () => {
        const options = [];
        React.Children.forEach(children, child => {
            if (child && child.type === 'option' && child.props && child.props.value) {
                options.push({
                    value: child.props.value,
                    label: child.props.children
                });
            }
        });
        return options;
    };
    const renderTimeDisplay = () => {
        if (!value) {
            return placeholder || '';
        }

        const [hours, minutes] = value.split(TIME_FORMAT_SEPARATOR);
        const hour = parseInt(hours, 10);
        const ampm = hour >= HOURS_IN_HALF_DAY ? PM_TEXT : AM_TEXT;
        const displayHour = hour % HOURS_IN_HALF_DAY || HOURS_IN_HALF_DAY;
        return `${displayHour.toString().padStart(MIN_PADDING_LENGTH, PADDING_CHAR)}${TIME_DISPLAY_SEPARATOR}${minutes} ${ampm}`;
    };
    return (
        <div className="custom-timeselect-container" ref={containerRef}>
            {isEditing ? (
                <div className="masked-input-container">
                    <input
                        ref={inputRef}
                        type="text"
                        className="masked-input-hidden"
                        onKeyDown={handleKeyPress}
                        onBlur={(e) => {
                            if (optionsRef.current && optionsRef.current.contains(e.relatedTarget)) {
                                return;
                            }
                            submitMaskedValue();
                        }}
                        autoFocus
                    />
                    <div
                        className="masked-input-display"
                        onClick={handleInputClick}
                    >
                        {maskedValue.split('').map((char, index) => (
                            <span
                                key={index}
                                className={`masked-char ${index === cursorPosition ? 'cursor-active' : ''}`}
                            >
                                {char}
                            </span>
                        ))}
                    </div>
                    {showOptions && (
                        <div
                            className="time-options-dropdown"
                            ref={optionsRef}
                        >
                            <div className="dropdown-label">{placeholder}</div>
                            {getOptions().map((option, index) => (
                                <div
                                    key={index}
                                    className={`time-option ${option.value === value ? 'selected' : ''}`}
                                    onClick={() => handleOptionSelect(option.value)}
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleOptionSelect(option.value);
                                        }
                                    }}
                                >
                                    {option.label}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <div
                    className="custom-timeselect-display"
                    onClick={handleFieldClick}
                    ref={ref}
                >
                    <div className="timeselect-value">{renderTimeDisplay()}</div>
                    <div className="timeselect-arrow"></div>
                </div>
            )}
        </div>
    );
});

export default CustomTimeSelect;